import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Send } from 'lucide-react';
import Navbaar from '../../../components/Navbar/Navbaar';
import Sidebar from '../../../components/Navbar/SideNavbar';
import { useSelector } from 'react-redux';
import api from '../../../api/axios';

const FACES = [
    { label: 'Bad',   emoji: '😕', color: '#F97066', sliderValue: 12 },
    { label: 'Ok',    emoji: '😐', color: '#FDB022', sliderValue: 37 },
    { label: 'Good',  emoji: '🙂', color: '#84CAFF', sliderValue: 63 },
    { label: 'Great', emoji: '😄', color: '#32D583', sliderValue: 88 },
];

const TOPICS = [
    'Job matching',
    'Hiring Signals',
    'AI Assistant',
    'Networking',
    'Daily Tasks',
    'Profile / CV',
    'Performance',
    'Pricing',
    'Something else',
];

const MAX_CHARS = 1000;

const Sk = ({ className = '' }) => (
    <div className={`animate-pulse bg-[#E9EBF0] rounded-lg ${className}`} />
);

const Feedback = () => {
    const [ready, setReady] = useState(false);
    const [sliderValue, setSliderValue] = useState(75);
    const [selectedTopics, setSelectedTopics] = useState([]);
    const [suggestion, setSuggestion] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    useEffect(() => { const t = setTimeout(() => setReady(true), 400); return () => clearTimeout(t); }, []);

    const activeIndex = sliderValue <= 25 ? 0 : sliderValue <= 50 ? 1 : sliderValue <= 75 ? 2 : 3;
    const trackRef = useRef(null);

    const getValueFromClientX = useCallback((clientX) => {
        const rect = trackRef.current?.getBoundingClientRect();
        if (!rect) return 0;
        const pct = (clientX - rect.left) / rect.width;
        return Math.max(0, Math.min(100, Math.round(pct * 100)));
    }, []);

    const handleTrackMouseDown = useCallback((e) => {
        e.preventDefault();
        setSliderValue(getValueFromClientX(e.clientX));
        const onMove = (ev) => setSliderValue(getValueFromClientX(ev.clientX));
        const onUp = () => {
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    }, [getValueFromClientX]);

    const toggleTopic = (t) =>
        setSelectedTopics(prev => prev.includes(t) ? prev.filter(x => x !== t) : [...prev, t]);

    const handleSubmit = async () => {
        if (submitting) return;
        setSubmitting(true);
        setErrorMsg('');
        try {
            await api.post('/api/feedback', {
                sliderValue,
                ratingIndex: activeIndex,
                topics: selectedTopics,
                suggestion,
            });
            setSubmitted(true);
            setTimeout(() => setSubmitted(false), 4000);
            setSliderValue(75);
            setSelectedTopics([]);
            setSuggestion('');
        } catch (err) {
            setErrorMsg(err?.response?.data?.message || 'Failed to submit. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    if (!ready) return (
        <div className="relative h-screen bg-[#F6F7FB] pt-16 flex flex-col">
            <div className="flex w-full"><Navbaar /></div>
            <div className="flex flex-row flex-1 min-h-0">
                <div className="hidden sm:flex flex-col w-64 border-r border-gray-200 bg-white flex-shrink-0"><Sidebar /></div>
                <div className="flex flex-1 overflow-y-auto w-full p-6">
                    <div className="w-full flex flex-col gap-4">
                        {/* rating card */}
                        <div className="bg-white border border-[#E6E8EE] rounded-2xl p-6">
                            <Sk className="h-5 w-44 mb-2" />
                            <Sk className="h-3.5 w-64 mb-6" />
                            <div className="flex justify-between items-end px-1 mb-5">
                                {[1,2,3,4].map(i => (
                                    <div key={i} className="flex flex-col items-center gap-2">
                                        <Sk className="w-12 h-12 rounded-full" />
                                        <Sk className="h-2.5 w-8" />
                                    </div>
                                ))}
                            </div>
                            <Sk className="h-6 w-full rounded-full" />
                        </div>
                        {/* topics card */}
                        <div className="bg-white border border-[#E6E8EE] rounded-2xl p-6">
                            <Sk className="h-5 w-40 mb-4" />
                            <div className="flex flex-wrap gap-2">
                                {[80,100,72,90,88,96,76,64,110].map((w,i) => (
                                    <Sk key={i} className={`h-8 rounded-full`} style={{ width: w }} />
                                ))}
                            </div>
                        </div>
                        {/* suggestion card */}
                        <div className="bg-white border border-[#E6E8EE] rounded-2xl p-6">
                            <Sk className="h-5 w-72 mb-2" />
                            <Sk className="h-3.5 w-96 mb-4" />
                            <Sk className="h-36 w-full rounded-xl mb-3" />
                            <div className="flex justify-end">
                                <Sk className="h-9 w-36 rounded-xl" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="relative h-screen bg-[#F6F7FB] pt-16 flex flex-col">
            <div className="flex w-full">
                <Navbaar />
            </div>
            <div className="flex flex-row flex-1 min-h-0 transition-all duration-300">
                <div className="hidden sm:flex flex-col w-64 border-r border-gray-200 bg-white flex-shrink-0">
                    <Sidebar />
                </div>

                <div className="flex flex-1 overflow-y-auto w-full p-6">
                    <div className="w-full flex flex-col gap-4">

                        {submitted && (
                            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-green-50 border border-green-200 text-sm text-green-800 font-medium font-dmsans">
                                <span>🎉</span> Thank you! Your feedback helps us improve Career Champ.
                            </div>
                        )}
                        {errorMsg && (
                            <div className="flex items-center gap-3 px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 font-medium font-dmsans">
                                <span>⚠️</span> {errorMsg}
                            </div>
                        )}

                        {/* ── Rating card ── */}
                        <div className="bg-white border border-[#E6E8EE] rounded-2xl p-6 text-left">
                            <h3 className="text-base font-semibold text-gray-900 mb-1">Rate your experience</h3>
                            <p className="text-sm text-gray-500 mb-6">Click a face or drag the slider to rate your experience.</p>

                            {/* Emoji faces — clickable */}
                            <div className="flex justify-between items-end px-1 mb-5">
                                {FACES.map((f, i) => {
                                    const active = i === activeIndex;
                                    return (
                                        <button
                                            key={f.label}
                                            type="button"
                                            onClick={() => setSliderValue(f.sliderValue)}
                                            className="flex flex-col items-center gap-2 transition-all duration-200 focus:outline-none"
                                            style={{ opacity: active ? 1 : 0.45 }}
                                        >
                                            <div
                                                className="flex items-center justify-center rounded-full transition-all duration-200"
                                                style={{
                                                    width: active ? 60 : 46,
                                                    height: active ? 60 : 46,
                                                    background: active ? f.color : '#F4F5F7',
                                                    fontSize: active ? 30 : 22,
                                                    boxShadow: active ? `0 8px 18px ${f.color}66` : 'none',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                {f.emoji}
                                            </div>
                                            <span className="text-xs font-medium text-gray-700">{f.label}</span>
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Custom drag slider */}
                            <div
                                ref={trackRef}
                                onMouseDown={handleTrackMouseDown}
                                className="relative select-none"
                                style={{ height: 36, cursor: 'pointer' }}
                            >
                                {/* Rainbow gradient track */}
                                <div className="absolute left-0 right-0 rounded-full" style={{
                                    top: '50%', transform: 'translateY(-50%)', height: 6,
                                    background: 'linear-gradient(90deg, #FECDCA 0%, #FEDF89 33%, #B2DDFF 66%, #ABEFC6 100%)',
                                }} />
                                {/* Blue fill */}
                                <div className="absolute left-0 rounded-full" style={{
                                    top: '50%', transform: 'translateY(-50%)', height: 6,
                                    width: `${sliderValue}%`,
                                    background: 'linear-gradient(90deg, #3F57E0, #6478F0)',
                                }} />
                                {/* Thumb */}
                                <div className="absolute rounded-full bg-white" style={{
                                    top: '50%', left: `${sliderValue}%`,
                                    transform: 'translate(-50%, -50%)',
                                    width: 22, height: 22,
                                    border: '3px solid #3F57E0',
                                    boxShadow: '0 4px 10px rgba(63,87,224,.35)',
                                    pointerEvents: 'none',
                                }} />
                            </div>

                            <div className="flex justify-between text-xs text-gray-400 mt-1">
                                <span>0</span><span>25</span><span>50</span><span>75</span><span>100</span>
                            </div>
                        </div>

                        {/* ── Topic chips ── */}
                        <div className="bg-white border border-[#E6E8EE] rounded-2xl p-6 text-left">
                            <h3 className="text-base font-semibold text-gray-900 mb-0.5">
                                What's this about?{' '}
                                <span className="text-gray-400 font-normal text-sm">(optional)</span>
                            </h3>
                            <div className="flex flex-wrap gap-2 mt-4">
                                {TOPICS.map(t => {
                                    const on = selectedTopics.includes(t);
                                    return (
                                        <button
                                            key={t}
                                            type="button"
                                            onClick={() => toggleTopic(t)}
                                            className="px-3.5 py-1.5 rounded-full text-sm font-medium border transition-all"
                                            style={{
                                                border: `1px solid ${on ? '#3F57E0' : '#E6E8EE'}`,
                                                background: on ? '#EEF1FE' : '#fff',
                                                color: on ? '#3F57E0' : '#334155',
                                            }}
                                        >
                                            {t}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* ── Suggestion ── */}
                        <div className="bg-white border border-[#E6E8EE] rounded-2xl p-6 text-left">
                            <h3 className="text-base font-semibold text-gray-900 mb-1">
                                Do you have any suggestions to improve our product?
                            </h3>
                            <p className="text-sm text-gray-500 mb-4">
                                Be as specific as you can — what were you trying to do, and what got in the way?
                            </p>
                            <textarea
                                value={suggestion}
                                onChange={e => setSuggestion(e.target.value.slice(0, MAX_CHARS))}
                                placeholder="Share your thoughts..."
                                rows={6}
                                className="w-full text-sm text-gray-700 border border-[#E6E8EE] rounded-xl p-3.5 resize-none bg-[#FAFAFB] focus:outline-none focus:ring-2 focus:ring-[#3F57E033] focus:border-[#3F57E0] transition-all leading-relaxed"
                            />
                            <div className="flex items-center justify-between mt-3">
                                <span className="text-xs text-gray-400">{suggestion.length} / {MAX_CHARS}</span>
                                <div className="flex gap-2.5">
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={submitting}
                                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-xl transition-opacity hover:opacity-90 disabled:opacity-50"
                                        style={{ backgroundColor: '#3F57E0' }}
                                    >
                                        <Send size={14} />
                                        {submitting ? 'Sending…' : 'Submit feedback'}
                                    </button>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

export default Feedback;
